import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { NewCustomerDelivery, NewCustomerRegistry } from '../../../interfaces/new-customer-requests.model';
import { NewCustomerService } from '../../../services/new-customer.service';
import {
  EMPTY_DELIVERY_NOTE,
  digitsOnlyControl,
  isValidTaxCode,
  isValidVat,
  taxCodeFormatValidator,
  upperCaseControl,
} from '../../../utils/new-customer.utils';

// Anagrafica scritta a mano quando la ricerca non trova l'azienda
@Component({
  selector: 'ord-new-customer-manual',
  imports: [MatButtonModule, MatCheckboxModule, MatFormFieldModule, MatInputModule, ReactiveFormsModule],
  templateUrl: './new-customer-manual.html',
  styleUrl: './new-customer-manual.scss',
})
export class NewCustomerManual implements OnInit {
  readonly flow = inject(NewCustomerService);
  private readonly formBuilder = inject(FormBuilder);
  private readonly destroyRef = inject(DestroyRef);

  readonly form = this.formBuilder.nonNullable.group({
    ragioneSociale: ['', [Validators.required, Validators.maxLength(35)]],
    codiceFiscale: ['', [Validators.required, taxCodeFormatValidator]],
    partitaIva: ['', Validators.required],
    indirizzo: ['', [Validators.required, Validators.maxLength(35)]],
    comune: ['', [Validators.required, Validators.maxLength(60)]],
    provincia: ['', Validators.required],
    cap: ['', Validators.required],
    useAsDelivery: [false],
  });

  ngOnInit(): void {
    // Il criterio dell'ultima ricerca precompila il suo campo; tornando indietro restano i dati scritti
    const registry = this.flow.registry();
    if (registry?.origine === 'Manuale') {
      this.form.patchValue({
        ragioneSociale: registry.ragioneSociale,
        codiceFiscale: registry.codiceFiscale ?? '',
        partitaIva: registry.partitaIva ?? '',
        indirizzo: registry.indirizzo ?? '',
        comune: registry.comune ?? '',
        provincia: registry.provincia ?? '',
        cap: registry.cap ?? '',
      }, { emitEvent: false });
    } else {
      const search = this.flow.searchRequest();
      this.form.patchValue({
        ragioneSociale: search.ragioneSociale ?? '',
        codiceFiscale: search.codiceFiscale ?? '',
        partitaIva: search.partitaIva ?? '',
      }, { emitEvent: false });
    }

    upperCaseControl(this.form.controls.codiceFiscale, this.destroyRef);
    upperCaseControl(this.form.controls.provincia, this.destroyRef);
    digitsOnlyControl(this.form.controls.partitaIva, this.destroyRef);
    digitsOnlyControl(this.form.controls.cap, this.destroyRef);

    // Si conserva subito: tornando indietro e poi avanti i dati restano
    this.form.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.flow.setRegistry(this.registry));
  }

  get registry(): NewCustomerRegistry {
    const value = this.form.getRawValue();
    return {
      origine: 'Manuale',
      ragioneSociale: value.ragioneSociale.trim(),
      codiceFiscale: value.codiceFiscale.trim() || null,
      partitaIva: value.partitaIva.trim() || null,
      indirizzo: value.indirizzo.trim() || null,
      cap: value.cap.trim() || null,
      comune: value.comune.trim() || null,
      provincia: value.provincia.trim() || null,
    };
  }

  get valid(): boolean {
    const value = this.form.getRawValue();
    return !!value.ragioneSociale.trim()
      && isValidTaxCode(value.codiceFiscale)
      && isValidVat(value.partitaIva)
      && !!value.indirizzo.trim()
      && !!value.comune.trim()
      && value.provincia.trim().length === 2
      && value.cap.trim().length === 5;
  }

  continue(): void {
    const registry = this.registry;
    this.flow.setRegistry(registry);

    if (this.form.controls.useAsDelivery.value) {
      const current = this.flow.delivery();
      const delivery: NewCustomerDelivery = current && current.branchId === null
        ? current
        : {
            branchId: null,
            tipoSede: null,
            riferimento: null,
            indirizzo: '',
            cap: '',
            citta: '',
            provincia: '',
            latitudine: null,
            longitudine: null,
            nota: { ...EMPTY_DELIVERY_NOTE },
          };

      this.flow.setDelivery({
        ...delivery,
        indirizzo: registry.indirizzo ?? '',
        cap: registry.cap ?? '',
        citta: registry.comune ?? '',
        provincia: registry.provincia ?? '',
      });
    }

    this.flow.go('deliveryForm');
  }
}
